import React, { useState } from "react";
import "./EngineeringWorkflowPanels.css";

function ReadinessBadge({ ready, note, variant = "engineering_detail" }) {
  const isQueryRepo = variant === "query_repository";
  const title = ready
    ? isQueryRepo
      ? "May be enough for a Feature Plan"
      : "Ready to implement (conservative)"
    : isQueryRepo
      ? "Not enough evidence yet for a strong Feature Plan"
      : "NOT ready to implement";
  return (
    <div className={`ewp-readiness ${ready ? "ready" : "not-ready"}`}>
      <strong>{title}</strong>
      {note && <p className="ewp-readiness-note">{note}</p>}
    </div>
  );
}

function CollapsibleSection({ title, defaultOpen = false, children, className = "" }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`ewp-section ${className}`.trim()}>
      <button type="button" className="ewp-section-toggle" onClick={() => setOpen(!open)}>
        {open ? "▼" : "▶"} {title}
      </button>
      {open && <div className="ewp-section-body">{children}</div>}
    </div>
  );
}

function planningReadinessLead(ir) {
  if (ir?.readyToImplementSafely) {
    return "Enough evidence may be available to create a Feature Plan.";
  }
  if (ir?.symbolTargetSurface?.located) {
    return "This answer found implementation evidence, but it is not yet enough for a strong Feature Plan.";
  }
  return "This repository answer is not yet enough to create a strong Feature Plan.";
}

function QueryRepositoryWorkflowPanels({
  ir,
  rt,
  fi,
  rb,
  cp,
  stage,
  artifactRepairAttempt,
  onCreateFeaturePlan,
}) {
  const showChangeImpact = stage === "feature_investigation" && fi;
  const symbolLocated = ir?.symbolTargetSurface?.located === true;

  return (
    <div className="engineering-workflow-panels ewp-query-repository" role="complementary" aria-label="Query Repository planning context">
      <p className="ewp-query-context-label">Query Repository investigation</p>

      {artifactRepairAttempt?.triggered && (
        <div className="ewp-repair-notice">
          Answer calibrated after validation
          {artifactRepairAttempt.repaired ? " (repaired)" : " (gaps remain)"}
        </div>
      )}

      {symbolLocated && ir.symbolTargetSurface?.locatedSummary && (
        <div className="ewp-located-summary">
          <strong>Implementation surface located</strong>
          <pre className="ewp-located-summary-text">{ir.symbolTargetSurface.locatedSummary}</pre>
        </div>
      )}

      {rb && (
        <CollapsibleSection title="Repository overview" defaultOpen={stage === "repo_brief"}>
          <p>{rb.whatItDoes}</p>
          {rb.primaryEntrypoints?.length > 0 && (
            <p><strong>Entrypoints:</strong> {rb.primaryEntrypoints.join(", ")}</p>
          )}
        </CollapsibleSection>
      )}

      {rt && (
        <CollapsibleSection title="Execution path notes" defaultOpen={stage === "runtime_trace"}>
          <p>
            Continuity: <strong>{(rt.continuityScore ?? 0).toFixed(2)}</strong>
            {rt.recoveredCriticalSymbols?.length > 0 && (
              <> · Recovered: {rt.recoveredCriticalSymbols.join(", ")}</>
            )}
          </p>
          {rt.orderedFlow?.length > 0 && (
            <ol className="ewp-trace-steps">
              {rt.orderedFlow.slice(0, 8).map((s) => (
                <li key={s.order}>
                  <code>{s.symbol}</code> @ {s.filePath}
                  <span className="ewp-step-why">{s.operationalWhy}</span>
                </li>
              ))}
            </ol>
          )}
          {rt.criticalUnknowns?.length > 0 && (
            <p className="ewp-unknowns"><strong>Unknowns:</strong> {rt.criticalUnknowns.join(", ")}</p>
          )}
        </CollapsibleSection>
      )}

      {!symbolLocated && (
      <CollapsibleSection title="Planning readiness" defaultOpen={false} className="ewp-planning-readiness">
        <p className="ewp-planning-lead">{planningReadinessLead(ir)}</p>
        <ReadinessBadge ready={ir.readyToImplementSafely} variant="query_repository" />

        {showChangeImpact && (
          <div className="ewp-change-impact">
            <strong>Change impact notes</strong>
            {fi.recommendedImplementationOrder?.length > 0 && (
              <ol>{fi.recommendedImplementationOrder.slice(0, 6).map((s, i) => <li key={i}>{s}</li>)}</ol>
            )}
            {fi.blastRadius?.length > 0 && (
              <p><strong>Blast radius:</strong> {fi.blastRadius.slice(0, 6).join(", ")}</p>
            )}
          </div>
        )}

        {ir.stillUnclear?.length > 0 && (
          <div>
            <strong>Still unclear for planning</strong>
            <ul>{ir.stillUnclear.slice(0, 5).map((u, i) => <li key={i}>{u}</li>)}</ul>
          </div>
        )}

        {ir.recommendedInvestigationsBeforeCoding?.length > 0 && !ir.readyToImplementSafely && (
          <div>
            <strong>Suggested next repo checks</strong>
            <ul>{ir.recommendedInvestigationsBeforeCoding.slice(0, 4).map((u, i) => <li key={i}>{u}</li>)}</ul>
          </div>
        )}

        {!ir.readyToImplementSafely && (
          <p className="ewp-planning-note">
            Feature Plans are where implementation guidance and preflight checks live.
            Continue investigating here first, or open Feature Plans when you are ready to plan a change.
          </p>
        )}

        {ir.readyToImplementSafely && cp?.handoffPrompt && (
          <details className="ewp-handoff-details">
            <summary>Cursor handoff preview</summary>
            <pre className="ewp-handoff">{cp.handoffPrompt}</pre>
          </details>
        )}
      </CollapsibleSection>
      )}

      {typeof onCreateFeaturePlan === "function" && (
        <div className="ewp-plan-action">
          <button type="button" className="ewp-create-plan-btn" onClick={onCreateFeaturePlan}>
            {ir.readyToImplementSafely ? "Create Feature Plan from this" : "Create Feature Plan with current evidence"}
          </button>
        </div>
      )}
    </div>
  );
}

function EngineeringDetailWorkflowPanels({
  ir,
  rt,
  fi,
  rb,
  cp,
  stage,
  artifactQualityValidation,
  artifactRepairAttempt,
  featureBuilderPreflight,
  cursorReadiness,
}) {
  const stageTitle = {
    repo_brief: "Repo Brief",
    runtime_trace: "Runtime Trace",
    feature_investigation: "Feature Investigation",
    implementation_readiness: "Implementation Readiness",
    cursor_preparation: "Cursor Preparation",
  }[stage] ?? stage;

  return (
    <div className="engineering-workflow-panels" role="complementary" aria-label="Engineering workflow">
      <div className="ewp-header">
        <span className="ewp-stage-badge">{stageTitle}</span>
        {artifactQualityValidation && (
          <span className={`ewp-quality ${artifactQualityValidation.passed ? "pass" : "fail"}`}>
            {artifactQualityValidation.passed ? "Quality OK" : "Quality gaps"}
          </span>
        )}
      </div>

      {artifactRepairAttempt?.triggered && (
        <div className="ewp-repair-notice">
          Answer calibrated after validation
          {artifactRepairAttempt.repaired ? " (repaired)" : " (gaps remain)"}
        </div>
      )}

      {rb && (
        <CollapsibleSection title="What this repo does" defaultOpen={stage === "repo_brief"}>
          <p>{rb.whatItDoes}</p>
          {rb.primaryEntrypoints?.length > 0 && (
            <p><strong>Entrypoints:</strong> {rb.primaryEntrypoints.join(", ")}</p>
          )}
          {rb.operationalRisks?.length > 0 && (
            <ul>{rb.operationalRisks.slice(0, 4).map((r, i) => <li key={i}>{r}</li>)}</ul>
          )}
        </CollapsibleSection>
      )}

      {rt && (
        <CollapsibleSection title="Runtime Trace" defaultOpen={stage === "runtime_trace"}>
          <p>
            Continuity: <strong>{(rt.continuityScore ?? 0).toFixed(2)}</strong>
            {rt.recoveredCriticalSymbols?.length > 0 && (
              <> · Recovered: {rt.recoveredCriticalSymbols.join(", ")}</>
            )}
          </p>
          {rt.orderedFlow?.length > 0 && (
            <ol className="ewp-trace-steps">
              {rt.orderedFlow.slice(0, 8).map((s) => (
                <li key={s.order}>
                  <code>{s.symbol}</code> @ {s.filePath}
                  <span className="ewp-step-why">{s.operationalWhy}</span>
                </li>
              ))}
            </ol>
          )}
          {rt.criticalUnknowns?.length > 0 && (
            <p className="ewp-unknowns"><strong>Unknowns:</strong> {rt.criticalUnknowns.join(", ")}</p>
          )}
        </CollapsibleSection>
      )}

      {fi && (
        <CollapsibleSection title="Feature Investigation" defaultOpen={stage === "feature_investigation"}>
          {fi.recommendedImplementationOrder?.length > 0 && (
            <ol>{fi.recommendedImplementationOrder.slice(0, 6).map((s, i) => <li key={i}>{s}</li>)}</ol>
          )}
          {fi.blastRadius?.length > 0 && (
            <p><strong>Blast radius:</strong> {fi.blastRadius.slice(0, 6).join(", ")}</p>
          )}
          {fi.highRiskAreas?.length > 0 && (
            <ul>{fi.highRiskAreas.slice(0, 4).map((r, i) => <li key={i}>{r}</li>)}</ul>
          )}
        </CollapsibleSection>
      )}

      <CollapsibleSection title="Implementation Readiness" defaultOpen>
        <ReadinessBadge ready={ir.readyToImplementSafely} note={ir.conservativeNote} />
        {ir.stillUnclear?.length > 0 && (
          <div>
            <strong>Still unclear</strong>
            <ul>{ir.stillUnclear.slice(0, 5).map((u, i) => <li key={i}>{u}</li>)}</ul>
          </div>
        )}
        {ir.recommendedInvestigationsBeforeCoding?.length > 0 && !ir.readyToImplementSafely && (
          <div>
            <strong>Before coding</strong>
            <ul>{ir.recommendedInvestigationsBeforeCoding.slice(0, 4).map((u, i) => <li key={i}>{u}</li>)}</ul>
          </div>
        )}
      </CollapsibleSection>

      {ir.readyToImplementSafely && cp ? (
        <CollapsibleSection title="Cursor Preparation">
          {cp.implementationOrder?.length > 0 && (
            <p><strong>Order:</strong> {cp.implementationOrder.slice(0, 4).join(" → ")}</p>
          )}
          {cp.handoffPrompt && (
            <pre className="ewp-handoff">{cp.handoffPrompt}</pre>
          )}
        </CollapsibleSection>
      ) : (
        <div className="ewp-handoff-blocked">
          Cursor handoff blocked until gaps are resolved.
          {cursorReadiness?.missingUnderstanding?.length > 0 && (
            <ul>{cursorReadiness.missingUnderstanding.slice(0, 3).map((u, i) => <li key={i}>{u}</li>)}</ul>
          )}
        </div>
      )}

      {featureBuilderPreflight && !featureBuilderPreflight.allowed && (
        <div className="ewp-fb-preflight">
          <strong>Feature Builder preflight: blocked</strong>
          <ul>{featureBuilderPreflight.blockingReasons.slice(0, 4).map((r, i) => <li key={i}>{r}</li>)}</ul>
          {featureBuilderPreflight.recommendedInvestigations?.length > 0 && (
            <p>Next: {featureBuilderPreflight.recommendedInvestigations[0]}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function EngineeringWorkflowPanels({
  engineeringWorkflowArtifacts,
  artifactQualityValidation,
  artifactRepairAttempt,
  featureBuilderPreflight,
  cursorReadiness,
  variant = "query_repository",
  onCreateFeaturePlan,
}) {
  if (!engineeringWorkflowArtifacts) return null;

  const ir = engineeringWorkflowArtifacts.implementationReadiness;
  const rt = engineeringWorkflowArtifacts.runtimeTrace;
  const fi = engineeringWorkflowArtifacts.featureInvestigation;
  const rb = engineeringWorkflowArtifacts.repoBrief;
  const cp = engineeringWorkflowArtifacts.cursorPreparation;
  const stage = engineeringWorkflowArtifacts.activeStage;

  if (variant === "query_repository") {
    return (
      <QueryRepositoryWorkflowPanels
        ir={ir}
        rt={rt}
        fi={fi}
        rb={rb}
        cp={cp}
        stage={stage}
        artifactRepairAttempt={artifactRepairAttempt}
        onCreateFeaturePlan={onCreateFeaturePlan}
      />
    );
  }

  return (
    <EngineeringDetailWorkflowPanels
      ir={ir}
      rt={rt}
      fi={fi}
      rb={rb}
      cp={cp}
      stage={stage}
      artifactQualityValidation={artifactQualityValidation}
      artifactRepairAttempt={artifactRepairAttempt}
      featureBuilderPreflight={featureBuilderPreflight}
      cursorReadiness={cursorReadiness}
    />
  );
}

export { planningReadinessLead, ReadinessBadge, CollapsibleSection };
